<template>
  <loader v-if="!page || !modules" />
  <div v-else>
    <div class="flex mb-6">
      <div v-if="editable" class="flex-grow">
        <input type="text" v-model="page.name" class="bg-transparent border-none w-full py-2" placeholder="Page Name" />
        <div v-if="errorBag.get('name')" class="text-red-dark text-sm">
          <i class="fas fa-exclamation-triangle"></i> {{ errorBag.get('name') }}
        </div>
      </div>
      <p v-else class="flex-grow">{{ page.name }}</p>
      <div>
        <slot name="actions"></slot>
        <button class="bg-white hover:bg-grey-light rounded border border-primary px-3 py-2" v-if="mode === 'view' && editable" @click="mode = 'edit'">
          <i class="fas fa-pencil-alt"></i> Edit Mode
        </button>
        <button class="bg-white hover:bg-grey-light rounded border border-primary px-3 py-2" v-if="mode === 'edit' && editable" @click="mode = 'view'">
          <i class="fas fa-eye"></i> Preview
        </button>
        <button class="bg-white hover:bg-grey-light rounded border border-primary px-3 py-2 ml-3" :disabled="page.group.type_id === 0" v-if="mode === 'edit' && editable" @click="viewDeletePage = true">
          <i class="fas fa-trash-alt"></i> Delete
        </button>

        <button v-if="editable" class="bg-primary text-white rounded border border-primary px-3 py-2 ml-3" @click="save">
          <loader fill-color="white" v-if="saving" />
          <span v-else><i class="fas fa-cloud-upload-alt"></i> Save Page Changes</span>
        </button>
      </div>
    </div>

    <div class="bg-white rounded border shadow">
      <div class="flex flex-col items-stretch">
        <div class="flex bg-white p-3 border-b rounded-t items-center pointer-events-none">
          <div class="flex-grow">
            <img v-if="logo || selectedOffice.logo" :src="logo || selectedOffice.logo" class="h-12" />
          </div>
          <div class="mr-3">
            <i class="fas fa-home"></i> Book a Property Valuation
          </div>
          <div class="w-32 h-8 mr-3 bg-grey-lighter border px-2 rounded flex items-center text-grey">
            <i class="fas fa-search"></i>
          </div>
          <button class="w-8 h-8 border rounded flex items-center justify-center">
            <i class="fas fa-bars"></i>
          </button>
        </div>

        <transition-group name="modules" tag="div" class="flex flex-wrap justify-center items-center">
          <div v-for="(m, i) in page.modules" :key="`${m.key}_module`" class="flex flex-col" :class="m.is_full_width ? 'w-full' : 'w-full md:w-1/2'">
            <!-- <button v-if="mode === 'edit'" class="border border-tertiary border-dashed p-3 m-3 text-right bg-white hover:bg-grey-lighter" @click="newModuleIndex = i">
              Add Module <i class="fas fa-plus-circle text-tertiary"></i>
            </button> -->

            <div :class="mode === 'edit' ? 'border border-tertiary border-dashed m-3 relative flex flex-col' : null">
              <div class="bg-red-dark text-white p-3" v-if="errorBag.getWithPrefix(`modules.${i}`)">
                <i class="fas fa-exclamation-triangle"></i> {{ errorBag.getWithPrefix(`modules.${i}`) }}
              </div>
              <div class="absolute pin-t pin-r -mt-4 -mr-2 z-20 flex border border-grey-dark rounded-full overflow-hidden">
                <button v-if="mode === 'edit'" class="rounded-l-full flex items-center justify-center h-6 px-2 border-r border-grey-dark bg-white" :disabled="getComponent(m.ezine_module_id).full_width_only" :class="getComponent(m.ezine_module_id).full_width_only ? 'text-grey-light cursor-default' : 'hover:bg-grey-light'" title="Toggle Width" @click="m.is_full_width = !m.is_full_width">
                  {{ m.is_full_width ? '100%' : '50%' }}
                </button>
                <button v-if="mode === 'edit'" class="flex items-center justify-center w-8 h-6 border-r border-grey-dark bg-white" :class="i === 0 ? 'text-grey-light cursor-default' : 'hover:bg-grey-light'" title="Move Up" @click="moveUp(m)">
                  <i class="fas fa-fw fa-arrow-up"></i>
                </button>
                <button v-if="mode === 'edit'" class="flex items-center justify-center w-8 h-6 border-grey-dark bg-white" :class="i === page.modules.length - 1 ? 'text-grey-light cursor-default' : 'hover:bg-grey-light'" title="Move Down" @click="moveDown(m)">
                  <i class="fas fa-fw fa-arrow-down"></i>
                </button>
                <button v-if="mode === 'edit' && !getComponent(m.ezine_module_id).locked" class="rounded-r-full flex items-center justify-center w-8 h-6 border-l border-grey-dark bg-white hover:bg-grey-light" title="Remove Module" @click="removeModule(m)">
                  <i class="fas fa-fw fa-times"></i>
                </button>
              </div>
              <component :is="getComponent(m.ezine_module_id).component" v-model="m.data" :mode="mode" :office-id="m.office_id" @office-change="m.office_id = $event" />
            </div>
          </div>
        </transition-group>

        <button v-if="mode === 'edit' && !isFrontPage" class="border border-tertiary border-dashed p-3 m-3 text-right bg-white hover:bg-grey-lighter" @click="newModuleIndex = page.modules.length">
          Add Module <i class="fas fa-plus-circle text-tertiary"></i>
        </button>
      </div>
    </div>

    <div class="flex justify-end my-6">
      <button class="bg-white hover:bg-grey-light rounded border border-primary px-3 py-2" v-if="mode === 'view' && editable" @click="mode = 'edit'">
        <i class="fas fa-pencil-alt"></i> Edit Mode
      </button>
      <button class="bg-white hover:bg-grey-light rounded border border-primary px-3 py-2" v-if="mode === 'edit' && editable" @click="mode = 'view'">
        <i class="fas fa-eye"></i> Preview
      </button>

      <button v-if="editable" class="bg-primary text-white rounded border border-primary px-3 py-2 ml-3" @click="save">
        <loader fill-color="white" v-if="saving" />
        <span v-else><i class="fas fa-cloud-upload-alt"></i> Save Page Changes</span>
      </button>
    </div>

    <modal v-if="viewDeletePage" @leave="viewDeletePage = false" show-close-btn width="600px">
      <p class="mb-6">Are you sure you want to permanently delete this page?</p>
      <button class="bg-white hover:bg-grey-light rounded border border-primary px-3 py-2 mr-3" v-if="!deletePage" @click="viewDeletePage = false">
        Cancel
      </button>
      <button v-if="editable" class="bg-primary text-white rounded border border-primary px-3 py-2" @click="deletePage">
        <loader fill-color="white" v-if="deleting" />
        <span v-else><i class="fas fa-trash-alt"></i> Delete Page</span>
      </button>
    </modal>

    <modal v-if="newModuleIndex !== null" @leave="newModuleIndex = null" show-close-btn width="240px">
      <h2 class="font-bold text-sm mb-1">New Module</h2>
      <p class="mb-6">Select the new module to add.</p>

      <div class="mb-6 flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 px-2 mb-4" v-for="m in unlockedModules" :key="m.component">
          <button @click="selectedNewModule = m" class="p-3 w-full rounded flex text-left items-center" :class="m === selectedNewModule ? 'bg-primary text-white' : 'bg-grey-lighter hover:bg-grey-light'">
            <div class="flex w-12 h-12 rounded bg-white items-center justify-center text-grey-darker">
              <i :class="[m.icon.indexOf('fab ') === 0 ? '' : 'fas', m.icon]"></i>
            </div>
            <div class="ml-3">
              <p>{{ m.name }}</p>
              <p class="text-xs text-grey-dark mt-1">{{ m.description }}</p>
            </div>
          </button>
        </div>
      </div>

      <div class="flex justify-end mt-6">
        <button class="bg-transparent text-white py-2 px-6 text-sm bg-green-dark rounded shadow" @click="addModule">
          <i class="fas fa-plus-circle"></i> Add Module
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import { mapActions, mapState } from 'vuex'
import ErrorBag from '@/ErrorBag'
import FrontCoverModule from '@/components/Ezines/Modules/FrontCoverModule'
import TextModule from '@/components/Ezines/Modules/TextModule'
import HeroImageModule from '@/components/Ezines/Modules/HeroImageModule'
import OfficesModule from '@/components/Ezines/Modules/OfficesModule'
import LocationsModule from '@/components/Ezines/Modules/LocationsModule'
import OfficeMapModule from '@/components/Ezines/Modules/OfficeMapModule'
import MeetTheTeamModule from '@/components/Ezines/Modules/MeetTheTeamModule'
import CallToActionModule from '@/components/Ezines/Modules/CallToActionModule'
import PropertiesModule from '@/components/Ezines/Modules/PropertiesModule'
import ImageModule from '@/components/Ezines/Modules/ImageModule'
import VideoModule from '@/components/Ezines/Modules/VideoModule'
import TwitterModule from '@/components/Ezines/Modules/TwitterModule'
import FacebookModule from '@/components/Ezines/Modules/FacebookModule'
import GuildBackCoverModule from '@/components/Ezines/Modules/GuildBackCoverModule'
import InstagramModule from '@/components/Ezines/Modules/InstagramModule'
import randomstring from 'randomstring'

export default {
  components: {
    FrontCoverModule,
    TextModule,
    HeroImageModule,
    OfficesModule,
    LocationsModule,
    OfficeMapModule,
    MeetTheTeamModule,
    CallToActionModule,
    PropertiesModule,
    ImageModule,
    VideoModule,
    TwitterModule,
    FacebookModule,
    GuildBackCoverModule,
    InstagramModule
  },
  props: {
    pageId: Number,
    logo: String
  },
  data () {
    return {
      mode: 'edit',
      page: null,
      newModuleIndex: null,
      modules: [],
      selectedNewModule: null,
      saving: false,
      errorBag: new ErrorBag(),
      viewDeletePage: false,
      deleting: false
    }
  },
  computed: {
    ...mapState(['selectedOffice']),
    unlockedModules () {
      return this.modules.filter((m) => !m.locked)
    },
    editable () {
      return this.page ? this.page.group.assignable_type !== 'App\\Models\\Network' : false
    },
    isFrontPage () {
      return this.page ? this.page.group.type_id === 0 : false
    }
  },
  created () {
    this.getModules()
    this.getPage()
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    getModules () {
      v2Api.get(`/ezine-modules`).then((response) => {
        this.modules = response.data
        this.selectedNewModule = this.modules.find((m) => !m.locked)
      })
    },
    getPage () {
      if (!this.pageId) {
        return
      }

      v2Api.get(`/ezine-pages/${this.pageId}`).then((response) => {
        for (let i = 0; i < response.data.modules.length; i++) {
          response.data.modules[i].key = randomstring.generate()
        }
        this.page = response.data
        if (!this.editable) {
          this.mode = 'view'
        }
      })
    },
    getComponent (moduleId) {
      return this.modules.find((m) => m.id === moduleId)
    },
    addModule () {
      this.page.modules.splice(this.newModuleIndex, 0, {
        key: randomstring.generate(),
        ezine_module_id: this.selectedNewModule.id,
        office_id: null,
        data: { ...this.selectedNewModule.default_config },
        is_full_width: true
      })
      this.newModuleIndex = null
    },
    removeModule (m) {
      this.page.modules.splice(this.page.modules.indexOf(m), 1)
    },
    moveUp (m) {
      let index = this.page.modules.indexOf(m)
      if (index === 0) {
        return
      }
      const removed = this.page.modules.splice(index, 1)[0]
      this.page.modules.splice(--index, 0, removed)
    },
    moveDown (m) {
      let index = this.page.modules.indexOf(m)
      if (index === this.page.modules.length - 1) {
        return
      }
      const removed = this.page.modules.splice(index, 1)[0]
      this.page.modules.splice(++index, 0, removed)
    },
    save () {
      this.saving = true
      this.errorBag.clear()

      v2Api.patch(`/ezine-pages/${this.pageId}`, this.page).then((response) => {
        this.page = response.data
        this.saving = false
        this.$emit('name-changed', this.page.name)

        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Page Saved',
          info: 'Your changes have been saved.'
        })
      }, (errors) => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: errors.response.data.message
        })
        this.errorBag.store(errors.response.data)
        this.saving = false
      })
    },
    deletePage () {
      this.deleting = true
      v2Api.delete(`/ezine-pages/${this.pageId}`).then((response) => {
        this.deleting = false
        this.$emit('deleted')

        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Page Deleted',
          info: 'The page has successfully deleted.'
        })
      }, (errors) => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: errors.response.data.message
        })
        this.errorBag.store(errors.response.data)
        this.deleting = false
      })
    }
  },
  watch: {
    pageId () {
      this.page = null
      this.getPage()
    }
  }
}
</script>

<style>
.modules-move,
.modules-enter-active,
.modules-leave-active {
  transition: all 0.5s ease;
}
.modules-enter-from,
.modules-leave-to {
  opacity: 0;
  transform: scale(.5);
}
.modules-leave-active {
  position: absolute;
}
</style>
